import classnames from "classnames";
import { Form, Formik } from "formik";
import React, { Fragment } from "react";
import {
    Button,
    Card,
    CardBody,
    CardFooter,
    Col,
    Row,
    Spinner,
} from "reactstrap";

import { getAuthenticationContext } from "@utils/configuration/aem";
import { FormInfoProvider } from "@utils/forms/formInfo.provider";
import * as ValidationRules from "@utils/validations";

import { translateWithoutContext } from "@redux/modules/value/translations/translations.helpers";

import PasswordField from "@components/Form/FormFields/PasswordField/passwordField.component";
import { MessageComponent } from "@components/General/Messages/messages.component";

type Props = {
    buttonText: string;
    onSubmit: (newPassword: string) => void;
    isLoading: boolean;
    isPreLoading: boolean;
    hasValidToken: boolean;
};

const FORM_ID = "loginForm";

const ResetPasswordForm = ({
    buttonText,
    onSubmit,
    isLoading,
    isPreLoading,
    hasValidToken,
}: Props) => {
    const onFormSubmit = ({ newPassword }: any) => {
        onSubmit(newPassword);
    };

    return (
        <Fragment>
            {isPreLoading && <Spinner color="primary" size="lg" />}
            {!isPreLoading && !hasValidToken && (
                <MessageComponent
                    message={{
                        id: "invalid-token",
                        title: translateWithoutContext(
                            "general.form.newPassword.error.title",
                        ),
                        text: translateWithoutContext(
                            "general.form.newPassword.error.message",
                            `<a href="${
                                getAuthenticationContext().requestPasswordUrl
                            }">${translateWithoutContext(
                                "general.form.newPassword.error.message.click",
                            )}</a>`,
                        ),
                        status: "danger",
                        dismissible: false,
                    }}
                />
            )}
            {!isPreLoading && hasValidToken && (
                <Card>
                    <div className={classnames("checkoutGuest d-md-block")}>
                        <FormInfoProvider
                            formId={FORM_ID}
                            withNeverbounce={false}
                        >
                            {() => (
                                <Formik
                                    enableReinitialize
                                    initialValues={{
                                        newPassword: "",
                                        newPasswordRepeat: "",
                                    }}
                                    onSubmit={onFormSubmit}
                                >
                                    {({ isSubmitting }) => (
                                        <Form id={FORM_ID}>
                                            <CardBody>
                                                <Row>
                                                    <Col md={12}>
                                                        <PasswordField
                                                            name="newPassword"
                                                            label={translateWithoutContext(
                                                                "general.form.newPassword.label",
                                                            )}
                                                            required
                                                            autoComplete={
                                                                "new-password"
                                                            }
                                                            placeholder={translateWithoutContext(
                                                                "general.form.newPassword.placeholder",
                                                            )}
                                                            validationRule={
                                                                ValidationRules.password
                                                            }
                                                        />
                                                    </Col>
                                                </Row>
                                                <Row>
                                                    <Col md={12}>
                                                        <PasswordField
                                                            name="newPasswordRepeat"
                                                            label={translateWithoutContext(
                                                                "general.form.newPasswordRepeat.label",
                                                            )}
                                                            required
                                                            autoComplete={
                                                                "new-password"
                                                            }
                                                            placeholder={translateWithoutContext(
                                                                "general.form.newPasswordRepeat.placeholder",
                                                            )}
                                                            validationRule={
                                                                ValidationRules.passwordRepeat
                                                            }
                                                        />
                                                    </Col>
                                                </Row>
                                            </CardBody>
                                            <CardFooter
                                                className={classnames(
                                                    "border-0 pb-2 px-2",
                                                )}
                                                style={{
                                                    backgroundColor: "inherit",
                                                }}
                                            >
                                                <Button
                                                    test="resetPasswordForm-button-submit"
                                                    type="submit"
                                                    size="lg"
                                                    color="primary"
                                                    loading={
                                                        isSubmitting &&
                                                        isLoading
                                                    }
                                                >
                                                    {buttonText}
                                                </Button>
                                            </CardFooter>
                                        </Form>
                                    )}
                                </Formik>
                            )}
                        </FormInfoProvider>
                    </div>
                </Card>
            )}
        </Fragment>
    );
};

export default ResetPasswordForm;
